/* pbm-quick-view-base.js
 * Must load BEFORE quick-view.js.
 * Provides the global DrawerComponent base class
 * needed by quick-view.js (which extends DrawerComponent).
 */

/* ── global stub ─────────────────────────────────────────── */
if (typeof window.pbmTheme === 'undefined') {
  window.pbmTheme = {
    QuickViewSectionId: null,

    utils: {
      getSectionId(el) {
        const section = el && el.closest && el.closest('[id^="shopify-section-"]');
        if (section) return section.id.replace('shopify-section-', '');
        if (el && el.dataset && el.dataset.sectionId) return el.dataset.sectionId;
        return null;
      },
    },

    a11y: {
      trapFocus(container, focusEl) {
        if (focusEl && focusEl.focus) {
          setTimeout(() => focusEl.focus(), 50);
        }
      },
      removeTrapFocus(el) {
        if (el && el.focus) el.focus();
      },
    },
  };
}

/* ── DrawerComponent base class ───────────────────────────────────── */
if (typeof window.DrawerComponent === 'undefined') {
  window.DrawerComponent = class DrawerComponent extends HTMLElement {
    constructor() {
      super();
    }

    /* Override in subclass to conditionally prevent show */
    shouleBeShow() { return true; }

    /* Override in subclass to do work just before the drawer opens */
    prepareToShow() {}

    /* Override in subclass to do work just after the drawer opens */
    handleAfterShow() {}

    /* Override in subclass to do work just after the drawer closes */
    handleAfterHide() {}

    get focusElement() {
      return this.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    }

    /* Whether to append to <body> when shown (needed for stacking context) */
    get requiresBodyAppended() { return true; }

    get overlay() {
      return this.querySelector('.fixed-overlay');
    }

    connectedCallback() {
      this._abortController = new AbortController();
      const signal = this._abortController.signal;

      /* All buttons that reference this modal via aria-controls */
      document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open ? this.hide() : this.show(e.currentTarget);
        }, { signal });
      });

      document.addEventListener('keyup', (e) => {
        if (e.key === 'Escape' && this.open) this.hide();
      }, { signal });
    }

    disconnectedCallback() {
      this._abortController && this._abortController.abort();
    }

    get open() { return this.hasAttribute('open'); }

    show(activeElement = null) {
      if (!this.shouleBeShow() || this.open) return;
      this._activeElement = activeElement;
      this.prepareToShow();

      /* Move to <body> so z-index stacking works */
      if (this.requiresBodyAppended && this.parentElement !== document.body) {
        this._originalParent = this.parentElement;
        document.body.appendChild(this);
      }

      this.hidden = false;
      this.removeAttribute('hidden');
      this.setAttribute('open', '');
      document.body.classList.add('modal-showing');

      /* Add [active] after paint so CSS transition fires */
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.setAttribute('active', '');
          document.body.classList.remove('modal-showing');
          document.body.classList.add('modal-show');
        });
      });

      setTimeout(() => this.handleAfterShow(), 300);
    }

    hide() {
      if (!this.open) return;
      this.removeAttribute('active');

      const inner = this.querySelector('.drawer__inner');
      const onEnd = () => {
        this.removeAttribute('open');
        this.hidden = true;
        document.body.classList.remove('modal-show');

        if (this._originalParent && this.parentElement === document.body) {
          this._originalParent.appendChild(this);
          this._originalParent = null;
        }

        this.handleAfterHide();
      };

      if (inner) {
        inner.addEventListener('transitionend', onEnd, { once: true });
      } else {
        setTimeout(onEnd, 400);
      }
    }
  };

  /* Register drawer-component custom element using the global class */
  if (!customElements.get('drawer-component')) {
    customElements.define('drawer-component', window.DrawerComponent);
  }
}
